'use client';

/**
 * Capture-grant prompt — the user-facing half of the pixel-true screenshot
 * handshake.
 *
 * An agent driving a widget over MCP has no user gesture, so it can never call
 * `getDisplayMedia` itself (the browser requires transient activation). When it
 * asks for a pixel-true screenshot and no capture session exists, the bridge
 * calls `requestCaptureGrant()` (host-capture.ts), which surfaces THIS modal.
 * The user's click on Enable capture IS the gesture that starts the one-time
 * screen-share session; after that every capture in the session is silent.
 *
 * Choices map to the grant outcome the agent hears back:
 *   • Enable capture   → 'granted'   (session live; retry the screenshot)
 *   • Not now          → 'dismissed' (declined this once; can ask again)
 *   • Don't ask again   → 'never'     (suppressed for this browser)
 *
 * Mounted once in the workspace shell, so it covers every widget on the tab.
 */

import { createPortal } from 'react-dom';
import { useT } from '@/components/i18n-provider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MonitorUp, ShieldCheck, X } from 'lucide-react';
import {
  registerGrantRequester,
  startCaptureSession,
  setCaptureGrantDismissedForever,
  type GrantOutcome,
} from '@/lib/workspaces/host-capture';

interface Pending {
  reason?: string;
  resolve: (o: GrantOutcome) => void;
}

export function CaptureGrantPrompt() {
  const t = useT();
  const [pending, setPending] = useState<Pending | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  pendingRef.current = pending;

  useEffect(() => {
    return registerGrantRequester(
      (reason?: string) =>
        new Promise<GrantOutcome>((resolve) => {
          // A superseding request resolves the previous one as dismissed.
          if (pendingRef.current) pendingRef.current.resolve('dismissed');
          setError(null);
          setStarting(false);
          setPending({ reason, resolve });
        }),
    );
  }, []);

  const settle = useCallback((outcome: GrantOutcome) => {
    const p = pendingRef.current;
    if (p) p.resolve(outcome);
    setPending(null);
    setStarting(false);
    setError(null);
  }, []);

  const enable = useCallback(async () => {
    setStarting(true);
    setError(null);
    try {
      // This click is the transient activation getDisplayMedia requires.
      const ok = await startCaptureSession();
      if (ok) settle('granted');
      else {
        setStarting(false);
        setError('Screen share was not started. You can try again.');
      }
    } catch {
      // User cancelled the browser picker, or capture is unavailable here.
      setStarting(false);
      setError('Screen share was cancelled. You can try again, or dismiss.');
    }
  }, [settle]);

  const never = useCallback(() => {
    setCaptureGrantDismissedForever(true);
    settle('never');
  }, [settle]);

  if (!pending || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4"
      onClick={() => settle('dismissed')}
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('workspace.capture.enableAria')}
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <MonitorUp className="h-4 w-4" />
            {t('workspace.capture.title')}
          </h2>
          <button
            onClick={() => settle('dismissed')}
            aria-label={t('workspace.canvas.node.close')}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <p className="text-xs leading-relaxed text-muted-foreground">
            The assistant wants a pixel-true screenshot of this app. Your browser needs a
            one-time permission to share this tab. Click{' '}
            <span className="font-medium text-foreground">{t('workspace.capture.enable')}</span>, then approve{' '}
            <span className="font-medium text-foreground">{t('workspace.capture.shareTab')}</span>.
          </p>
          <p className="flex items-start gap-2 rounded-lg bg-muted/40 p-2.5 text-[11px] leading-relaxed text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-500" />
            <span>{t('workspace.capture.scopeNote')}</span>
          </p>
          {error ? <p className="text-[11px] text-amber-500">{error}</p> : null}

          <button
            onClick={() => void enable()}
            disabled={starting}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-foreground px-3 py-2.5 text-xs font-semibold text-background hover:opacity-90 disabled:opacity-60"
          >
            <MonitorUp className="h-3.5 w-3.5" />
            {starting ? t('workspace.capture.waiting') : t('workspace.capture.enable')}
          </button>
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => settle('dismissed')}
              className="flex-1 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-accent"
            >
              {t('push.notNow')}
            </button>
            <button
              onClick={never}
              className="flex-1 rounded-lg px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
            >
              Don&apos;t ask again
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
